import type React from "react";
import { useCanvasStore } from "../store/canvas-store";
import { ZoomButtons } from "./zoom-buttons";
import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "../../../lib/canvas-coordinates";

// 右下のミニマップ（全体地図 MAP）。
// - 8K キャンバス全体を縮小表示し、現在のビューポート位置を四角で囲って表示する
// - ミニマップ内をクリックすると該当位置へ pan する（クリック位置をビューポート中央に揃える）
// - 隣接して - / + ズームボタンを配置
const MINIMAP_WIDTH_PX = 220;
const MINIMAP_HEIGHT_PX = Math.round((CANVAS_HEIGHT_PX / CANVAS_WIDTH_PX) * MINIMAP_WIDTH_PX);

export function MiniMap(): JSX.Element {
  const view = useCanvasStore((s) => s.view);
  const setView = useCanvasStore((s) => s.setView);
  // ビューポート（screen 座標で表示中の領域）が stage 座標でどこにあたるかを概算
  const viewportWidthInStage = window.innerWidth / view.zoom;
  const viewportHeightInStage = window.innerHeight / view.zoom;
  const viewportLeftInStage = -view.panX / view.zoom;
  const viewportTopInStage = -view.panY / view.zoom;

  const ratioX = MINIMAP_WIDTH_PX / CANVAS_WIDTH_PX;
  const ratioY = MINIMAP_HEIGHT_PX / CANVAS_HEIGHT_PX;

  // ミニマップ内クリック → 該当ステージ座標がビューポート中心になるよう pan を更新する。
  const handleMapClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const rect = event.currentTarget.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    const stageX = localX / ratioX;
    const stageY = localY / ratioY;
    setView({
      ...view,
      panX: window.innerWidth / 2 - stageX * view.zoom,
      panY: window.innerHeight / 2 - stageY * view.zoom,
    });
  };

  return (
    <div
      style={{
        position: "absolute",
        right: "16px",
        bottom: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        zIndex: 10,
      }}
    >
      <div
        data-testid="mini-map"
        onClick={handleMapClick}
        style={{
          width: `${MINIMAP_WIDTH_PX}px`,
          height: `${MINIMAP_HEIGHT_PX}px`,
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-1)",
          borderRadius: "var(--radius-2)",
          position: "relative",
          overflow: "hidden",
          cursor: "pointer",
        }}
      >
        <div
          data-testid="mini-map-viewport-frame"
          style={{
            position: "absolute",
            left: `${Math.max(0, viewportLeftInStage * ratioX)}px`,
            top: `${Math.max(0, viewportTopInStage * ratioY)}px`,
            width: `${Math.min(MINIMAP_WIDTH_PX, viewportWidthInStage * ratioX)}px`,
            height: `${Math.min(MINIMAP_HEIGHT_PX, viewportHeightInStage * ratioY)}px`,
            border: "2px solid var(--color-accent)",
            borderRadius: "2px",
            pointerEvents: "none",
          }}
        />
      </div>
      <ZoomButtons />
    </div>
  );
}
